import React, { useState, useEffect } from "react";
import "./CallLists.css"; // Reusing the same CSS file for consistent styling
import DoctorChip from "./DoctorChip";
import * as ApiService from "../services/ApiService";
import { useAuth0 } from "@auth0/auth0-react";

const DoctorEligibility = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [callLists, setCallLists] = useState([]);
  const [allDoctors, setAllDoctors] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCallList, setEditingCallList] = useState(null);
  const [selectedDoctors, setSelectedDoctors] = useState(new Set());
  const [hoveredDoctor, setHoveredDoctor] = useState(null);
  const [doctorDetails, setDoctorDetails] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  // New state variables for metrics
  const [metricsData, setMetricsData] = useState([]);
  const [showSeedingNotification, setShowSeedingNotification] = useState(false);
  const [doctorsNeedingSeeding, setDoctorsNeedingSeeding] = useState([]);
  const [medianTotal, setMedianTotal] = useState(0);

  useEffect(() => {
    fetchCallLists();
    fetchAllDoctors();
  }, []);

  useEffect(() => {
    if (hoveredDoctor) {
      fetchDoctorDetails(hoveredDoctor);
    } else {
      setDoctorDetails(null);
    }
  }, [hoveredDoctor]);

  const fetchCallLists = async () => {
    try {
      const data = await ApiService.fetchCallLists(getAccessTokenSilently);
      // Sort the data: mainline first, then adjunct
      const sortedData = [...data].sort((a, b) => {
        // Use spread to avoid mutating original data if needed elsewhere
        if (a.type === "mainline" && b.type !== "mainline") {
          return -1; // a (mainline) comes before b (adjunct)
        }
        if (a.type !== "mainline" && b.type === "mainline") {
          return 1; // b (mainline) comes before a (adjunct)
        }
        // Optional: Maintain original relative order or add secondary sort (e.g., by name) for lists of the same type
        // For now, we keep the original relative order if types are the same
        return 0;
      });
      setCallLists(sortedData);
    } catch (error) {
      console.error("Error fetching call lists:", error);
    }
  };

  const fetchAllDoctors = async () => {
    try {
      const data = await ApiService.fetchDoctors(getAccessTokenSilently);
      setAllDoctors(data);
    } catch (error) {
      console.error("Error fetching doctors:", error);
    }
  };

  const fetchDoctorDetails = async (staffId) => {
    try {
      const data = await ApiService.getDoctorDetails(
        getAccessTokenSilently,
        staffId
      );
      setDoctorDetails(data);
    } catch (error) {
      console.error("Error fetching doctor details:", error);
    }
  };

  // New function to fetch metrics data for a call list
  const fetchMetricsData = async (callListId) => {
    try {
      const data = await ApiService.getMetrics(
        getAccessTokenSilently,
        callListId,
        "overall"
      );
      setMetricsData(data);
      return data;
    } catch (error) {
      console.error("Error fetching metrics:", error);
      return [];
    }
  };

  // Calculate median total calls
  const calculateMedianTotal = (metricsData) => {
    if (!metricsData.length) return 0;

    const totalCalls = metricsData.map(
      (d) => d.regularWeekdays + d.weekends + d.publicHolidays
    );
    const sorted = [...totalCalls].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);

    if (sorted.length % 2 === 0) {
      return Math.round((sorted[middle - 1] + sorted[middle]) / 2);
    } else {
      return Math.round(sorted[middle]);
    }
  };

  // Check for doctors with low call counts (below 60% of median)
  const checkForLowCallCounts = (metricsData, newDoctorIds) => {
    if (!metricsData.length || !newDoctorIds.length) return [];

    const median = calculateMedianTotal(metricsData);
    setMedianTotal(median);

    // Find doctors with total calls below 60% of median
    const threshold = median * 0.6;

    // Get metrics for newly added doctors
    const newDoctorsMetrics = metricsData.filter((doctor) =>
      newDoctorIds.includes(doctor.doctorId)
    );

    // Filter doctors with low call counts
    const lowCallDoctors = newDoctorsMetrics.filter((doctor) => {
      const totalCalls =
        doctor.regularWeekdays + doctor.weekends + doctor.publicHolidays;
      return totalCalls < threshold;
    });

    return lowCallDoctors;
  };

  const handleEditClick = (callList) => {
    setEditingCallList(callList);
    const selectedIds = new Set(callList.Doctors.map((d) => d.staffId));
    setSelectedDoctors(selectedIds);
    setShowEditModal(true);
  };

  const handleDoctorClick = (doctorId) => {
    const newSelected = new Set(selectedDoctors);
    if (newSelected.has(doctorId)) {
      newSelected.delete(doctorId);
    } else {
      newSelected.add(doctorId);
    }
    setSelectedDoctors(newSelected);
  };

  const handleSelectAll = () => {
    // If all doctors are already selected, deselect all. Otherwise, select all.
    const allDoctorIds = allDoctors.map((doctor) => doctor.staffId);
    if (selectedDoctors.size === allDoctors.length) {
      setSelectedDoctors(new Set());
    } else {
      setSelectedDoctors(new Set(allDoctorIds));
    }
  };

  const handleSave = async () => {
    try {
      // Get the current doctors in the call list before updating
      const currentDoctorIds = new Set(
        editingCallList.Doctors.map((d) => d.staffId)
      );

      // Get the new doctor IDs that weren't in the call list before
      const newDoctorIds = Array.from(selectedDoctors).filter(
        (id) => !currentDoctorIds.has(id)
      );

      const response = await ApiService.updateCallListDoctors(
        getAccessTokenSilently,
        editingCallList.callListTypeId,
        Array.from(selectedDoctors)
      );

      if (response.ok) {
        // If new doctors were added, check their call counts
        if (newDoctorIds.length > 0) {
          const metricsData = await fetchMetricsData(
            editingCallList.callListTypeId
          );
          const lowCallDoctors = checkForLowCallCounts(
            metricsData,
            newDoctorIds
          );

          if (lowCallDoctors.length > 0) {
            setDoctorsNeedingSeeding(lowCallDoctors);
            setShowSeedingNotification(true);
          }
        }

        await fetchCallLists();
        setShowEditModal(false);
      }
    } catch (error) {
      console.error("Error saving doctor memberships:", error);
    }
  };

  const handleMouseEnter = (e, staffId) => {
    const rect = e.target.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + window.scrollX,
      y: rect.bottom + window.scrollY,
    });
    setHoveredDoctor(staffId);
  };

  const handleMouseLeave = () => {
    setHoveredDoctor(null);
  };

  const handleCloseSeedingNotification = () => {
    setShowSeedingNotification(false);
    setDoctorsNeedingSeeding([]);
  };

  return (
    <div className="page-container">
      <div className="staff-header">
        <h2>Doctor Eligibility Management</h2>
      </div>

      {/* Seeding Notification */}
      {showSeedingNotification && doctorsNeedingSeeding.length > 0 && (
        <div className="seeding-notification">
          <div className="seeding-notification-content">
            <div className="seeding-notification-header">
              <h3>Low Call Count</h3>
              <button
                className="close-notification-button"
                onClick={handleCloseSeedingNotification}
              >
                ×
              </button>
            </div>
            <p>
              These doctors' total calls are quite below the average amount
              (less than 60% of the median of {medianTotal} calls) for{" "}
              <strong>{editingCallList?.name}</strong>.
            </p>
            <p>
              Please consider seeding them the median amount of calls on the
              Metrics page.
            </p>
            <div className="doctors-needing-seeding">
              {doctorsNeedingSeeding.map((doctor) => (
                <DoctorChip
                  key={doctor.doctorId}
                  doctor={{
                    staffId: doctor.doctorId,
                    abbreviation: doctor.doctorAbbreviation,
                  }}
                />
              ))}
            </div>
            <div className="seeding-notification-actions">
              <button
                className="seeding-notification-button"
                onClick={handleCloseSeedingNotification}
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}

      {callLists.length === 0 ? (
        <div className="no-data-message">No call lists exist yet.</div>
      ) : (
        <div className="eligibility-grid">
          {callLists.map((callList) => (
            <div
              key={callList.callListTypeId}
              className={`eligibility-box ${
                !callList.active ? "inactive" : ""
              }`}
            >
              <div className="eligibility-header">
                <h3>
                  {callList.name}
                  {!callList.active && (
                    <span className="status-label inactive">inactive</span>
                  )}
                </h3>
                <button
                  className="edit-button"
                  onClick={() => handleEditClick(callList)}
                >
                  Edit
                </button>
              </div>
              <div className="eligible-doctors">
                {callList.Doctors &&
                  callList.Doctors.map((doctor) => (
                    <DoctorChip key={doctor.staffId} doctor={doctor} />
                  ))}
                {(!callList.Doctors || callList.Doctors.length === 0) && (
                  <span className="no-doctors">No doctors assigned</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showEditModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Edit {editingCallList.name} Eligibility</h3>
            <div className="modal-description">
              Click on doctors to toggle their eligibility for this call list.
              Selected doctors will be eligible for{" "}
              <strong>{editingCallList.name}</strong>.
            </div>

            {allDoctors.length === 0 ? (
              <div className="no-doctors-message">
                No doctors have been added yet. Please add doctors in the Staff
                Management section first.
              </div>
            ) : (
              <>
                <div className="doctor-selection-grid">
                  {allDoctors.map((doctor) => (
                    <DoctorChip
                      key={doctor.staffId}
                      doctor={doctor}
                      selectable={true}
                      selected={selectedDoctors.has(doctor.staffId)}
                      onClick={handleDoctorClick}
                    />
                  ))}
                </div>

                <button
                  className="select-all-button"
                  onClick={handleSelectAll}
                  style={{ margin: "10px 0" }}
                >
                  {selectedDoctors.size === allDoctors.length
                    ? "Deselect All"
                    : "Select All"}{" "}
                  Doctors
                </button>
              </>
            )}

            <div className="modal-buttons">
              <button onClick={handleSave}>Save Changes</button>
              <button onClick={() => setShowEditModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorEligibility;
